import { z } from "zod";
import { addressFields, nameSchema, objectIdSchema, optionalEmailSchema, optionalPhoneSchema, phoneSchema, slugSchema, } from "./common.js";
import { USER_ROLES } from "../constants/roles.js";
import { CLONABLE_TIERS, MAX_TIER_LEVELS } from "./organization.js";
export const SUPPLIER_TYPES = ["org", "depot", "distributor", "sub_distributor"];
export const supplierTypeSchema = z.enum(SUPPLIER_TYPES);
export const createSalesRepSchema = z.object({
    name: nameSchema,
    email: optionalEmailSchema,
    phone: phoneSchema,
    /** The same form creates every clonable tier; defaults to sales rep. */
    role: z.enum(CLONABLE_TIERS).optional(),
    level: z.number().int().min(1).max(MAX_TIER_LEVELS).optional(),
    /** Required from level 2 down: the level-above person they report to. */
    parentId: objectIdSchema.optional(),
});
/**
 * Moving a person to another sales role — a sub-distributor promoted to
 * distributor. Same account and history; the counters they looked after move
 * with them (D-17).
 */
export const changeRoleSchema = z.object({
    role: z.enum(CLONABLE_TIERS),
    level: z.number().int().min(1).max(MAX_TIER_LEVELS),
    /** Required when the new level reports to someone. */
    parentId: objectIdSchema.optional(),
});
export const createCounterSchema = z.object({
    name: nameSchema,
    email: optionalEmailSchema,
    phone: phoneSchema,
    /** Optional — generated from the name when omitted. Immutable once set. */
    slug: slugSchema.optional(),
    salesRepId: objectIdSchema.optional(),
    /*
     * The same place fields a depot takes. State and city stay OPTIONAL here,
     * unlike on a depot: counters are added in bulk and from the field, and a
     * required state would block someone standing in the shop with the owner.
     */
    state: z.string().trim().max(100).optional(),
    city: z.string().trim().max(100).optional(),
    ...addressFields,
    supplierType: z.enum(SUPPLIER_TYPES).optional(),
    supplierId: z.string().optional(),
    depotId: z.string().optional(),
    subDistributorId: z.string().optional(),
});
export const updateCounterSchema = z.object({
    name: nameSchema.optional(),
    phone: optionalPhoneSchema,
    salesRepId: objectIdSchema.nullable().optional(),
    state: z.string().trim().max(100).optional(),
    city: z.string().trim().max(100).optional(),
    ...addressFields,
    supplierType: z.enum(SUPPLIER_TYPES).optional(),
    supplierId: z.string().nullable().optional(),
    depotId: z.string().nullable().optional(),
    subDistributorId: z.string().nullable().optional(),
});
export const createUserSchema = z.object({
    name: nameSchema,
    email: optionalEmailSchema,
    phone: phoneSchema,
    role: z.enum(USER_ROLES).optional(),
    /* Counters are created through this route too, and they carry a place. */
    state: z.string().trim().max(100).optional(),
    city: z.string().trim().max(100).optional(),
    ...addressFields,
    supplierType: z.enum(SUPPLIER_TYPES).optional(),
    supplierId: z.string().optional(),
    depotId: z.string().optional(),
    subDistributorId: z.string().optional(),
});
/** Org admin creating a karigar by hand, alongside the QR path. */
const karigarPlaceFields = {
    district: z.string().trim().max(100).optional(),
    village: z.string().trim().max(100).optional(),
    city: z.string().trim().min(1, "Enter your city").max(100),
    state: z.string().trim().min(1, "Choose your state").max(100),
    pincode: z.string().trim().regex(/^[1-9]\d{5}$/, "Enter a valid 6-digit PIN code"),
};
export const createKarigarSchema = z.object({
    ...karigarPlaceFields,
    name: nameSchema,
    phone: phoneSchema,
    counterId: objectIdSchema,
    address: z.string().trim().max(300).optional(),
});
/** D-09 mitigation: a carpenter who changes shops is moved, not duplicated. */
export const transferKarigarSchema = z.object({
    counterId: objectIdSchema,
    reason: z.string().trim().max(300).optional(),
});
/** Public self-registration from the counter's QR poster. */
/** The registration form. No phone: it comes from the WhatsApp link (D-22). */
export const karigarRegistrationSchema = z.object({
    ...karigarPlaceFields,
    name: nameSchema,
    address: z.string().trim().min(4, "Enter your address").max(300),
    /** Object key returned by the photo upload step. Optional. */
    photoKey: z.string().trim().max(300).optional(),
});
export const approvalActionSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("approve") }),
    z.object({
        action: z.literal("reject"),
        reason: z.string().trim().min(4, "Give a reason — it is sent to the karigar").max(300),
    }),
]);
//# sourceMappingURL=user.js.map